import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { debounceTime, Subject, switchMap, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EventEmitterService } from '../../services/event-emitter.service';
import { TablesService } from '../../services/tables.service';
import { BranchesService } from '../../services/branches.service';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  standalone: false,
  selector: 'app-table-popup',
  templateUrl: './table-popup.component.html',
  styleUrl: './table-popup.component.scss',
})
export class TablePopupComponent implements OnInit, AfterViewInit {
  user;
  title;
  type = '';
  formGroup: FormGroup;
  table;
  tableUrl = '';
  qrCodeDownloadLink: SafeUrl = '';
  branches = [];
  selectedBranch = null;
  disableButton = false;

  searchBranches = new Subject<string>();
  searchBranchesNoResults = 'no-results-found';

  constructor(
    public bsModalRef: BsModalRef,
    private tablesService: TablesService,
    private branchesService: BranchesService,
    private eventEmitterService: EventEmitterService,
    private authenticationService: AuthenticationService,
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {}

  async ngOnInit() {
    await this.getUser();
    this.setupForm();
    this.setupTableUrl();
    this.getAllBranches('');
  }

  ngAfterViewInit(): void {
    this.setupBranchesSearch();
  }

  async getUser() {
    this.user = await this.authenticationService.getUserFromToken();
  }

  setupForm() {
    this.formGroup = new FormGroup({
      name: new FormControl(
        this.table ? this.table.name : '',
        Validators.required,
      ),
      branch_id: new FormControl(
        this.table ? this.table.branch_id : null,
        Validators.required,
      ),
      special_id: new FormControl(
        this.table ? this.table.special_id : this.generateSpecialId(),
        Validators.required,
      ),
    });
  }

  // 20 random bytes as hex (40 chars), the backend validates this same format
  generateSpecialId() {
    const bytes = new Uint8Array(20);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  }

  get qrCodeRegenerated() {
    return (
      this.table &&
      this.formGroup.get('special_id').value != this.table.special_id
    );
  }

  regenerateQrCode() {
    this.formGroup.get('special_id').setValue(this.generateSpecialId());
    this.setupTableUrl();
  }

  setupTableUrl() {
    // Same route the store uses for table ordering: /:business_name/table/:table_id
    this.tableUrl = `${window.location.origin}/${this.user.user.business.name}/table/${this.formGroup.get('special_id').value}`;
  }

  getAllBranches(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.branchesService.getAllBranches(apiData).subscribe(
      (response: any) => {
        this.searchBranchesNoResults = 'no-results-found';
        this.branches = response.items;

        if (this.table && this.table.branch_id) {
          this.selectedBranch = this.branches.find(
            (branch) => branch.id == this.table.branch_id,
          );
        } else if (this.branches.length == 1) {
          this.selectedBranch = this.branches[0];
          this.formGroup.get('branch_id').setValue(this.branches[0].id);
        }
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchBranchesNoResults = 'no-results-found';
      },
    );
  }

  setupBranchesSearch() {
    this.searchBranches
      .pipe(
        tap(() => (this.searchBranchesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllBranches(searchText)),
      )
      .subscribe();
  }

  setBranch(event) {
    this.selectedBranch = event.value;
    this.formGroup
      .get('branch_id')
      .setValue(event.value ? event.value.id : null);
  }

  onQrCodeUrlChange(url: SafeUrl) {
    this.qrCodeDownloadLink = url;
  }

  copyTableUrl() {
    navigator.clipboard.writeText(this.tableUrl).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('success'),
        detail: this.translateService.instant('link-copied'),
      });
    });
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  buttonClicked() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }

    if (this.disableButton == true) {
      return;
    }
    this.disableButton = true;

    const formValue = { ...this.formGroup.value };

    if (this.type === 'new-table') {
      this.tablesService.createTable(formValue).subscribe(
        (response) => {
          this.eventEmitterService.callAllTables();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:');
          console.log(error);
          this.disableButton = false;
        },
      );
    }
    if (this.type === 'edit-table') {
      this.tablesService.updateTable(this.table.id, formValue).subscribe(
        (response) => {
          this.eventEmitterService.callAllTables();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:');
          console.log(error);
          this.disableButton = false;
        },
      );
    }
  }
}
